// StormsListView.js
import React, { useState, useEffect } from 'react';
import { Table, message, Input, Select, Form, Button, Modal, DatePicker, Collapse, List, Spin, Checkbox, Popconfirm, Card, Typography, Row, Col, Tag, Tooltip, Space, Tabs } from 'antd';
import { Box } from '@mui/material';
import api from "../../../authentication/axiosInstance";
import { 
  EditOutlined, 
  CloudOutlined, 
  ThunderboltOutlined, 
  FilterOutlined, 
  SearchOutlined, 
  PlusOutlined, 
  InfoCircleOutlined, 
  ReloadOutlined,
  SaveOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  BarsOutlined,
  LoadingOutlined,
  InfoCircleFilled,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  RadarChartOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import styles from './StormListView.module.css';

dayjs.extend(utc);
dayjs.extend(timezone);
const { Option } = Select;
const { Title, Text } = Typography;

const StormsListView = () => {
  const [storms, setStorms] = useState([]);

  const [searchStormType, setSearchStormType] = useState(undefined);
  const [searchStormSeverity, setSearchStormSeverity] = useState(undefined);
  const [searchRegion, setSearchRegion] = useState(undefined);
  const [filteredStorms, setFilteredStorms] = useState([]);
  const [regions, setRegions] = useState([]);

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingStorm, setEditingStorm] = useState(null);
  const [editForm] = Form.useForm();
  const [stormThreats, setStormThreats] = useState([]);
  const [relevantThreats, setRelevantThreats] = useState([]);
  const [loadingRelevantThreats, setLoadingRelevantThreats] = useState(false);

  const [threats, setThreats] = useState([]);
  const [selectedThreats, setSelectedThreats] = useState([]);
  const [loadingThreats, setLoadingThreats] = useState(false);
  const [importingThreats, setImportingThreats] = useState(false);
  const [savingThreats, setSavingThreats] = useState(false);
  
  const [savedThreats, setSavedThreats] = useState([]);
  const [loadingSavedThreats, setLoadingSavedThreats] = useState(false);
  const [verifyingThreat, setVerifyingThreat] = useState(false);

  const [isViewDetailsModalVisible, setIsViewDetailsModalVisible] = useState(false);
  const [selectedStorm, setSelectedStorm] = useState(null);
  const [selectedStorms, setSelectedStorms] = useState([]);
  const [year, setYear] = useState(null);
  const [fetchedStorms, setFetchedStorms] = useState(null);
  const [loadingStorms, setLoadingStorms] = useState(false);
  const [searchCity, setSearchCity] = useState("");

  const fetchStorms = async () => {
    try {
      const response = await api.get('/dispatch/storm/');
      setStorms(response.data);
      setFilteredStorms(response.data);
    } catch (error) {
      console.error('Error fetching storms:', error);
      message.error('Failed to fetch storms');
    }
  }

  const fetchRegions = async () => {
    try {
      const response = await api.get('/sites/site_regions/');
      setRegions(response.data);
    } catch (error) {
      console.error('Error fetching regions:', error);
      message.error('Failed to fetch regions');
    }
  }

  const fetchSavedThreats = async () => {
    setLoadingSavedThreats(true);
    try {
      const response = await api.get('/weatherworks/saved-threats/');
      if (response.data.success) {
        setSavedThreats(response.data.threats);
      }
    } catch (error) {
      console.error('Error fetching saved threats:', error);
      message.error('Failed to fetch saved threats');
    } finally {
      setLoadingSavedThreats(false);
    }
  };

  useEffect(() => {
    fetchStorms();
    fetchRegions();
    fetchSavedThreats();
  },[])

  const capitalize = (text) => {
    if (!text) return "N/A"; // Return "N/A" if text is null or undefined
    return text
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  };

  const showEditModal = (storm) => {
    setEditingStorm(storm);
  
    editForm.setFieldsValue({
      type: storm.type,
      severity: storm.severity,
      region: storm.region,
      storm_strat_sent: storm.storm_strat_sent,
      date_of_storm: storm.date_of_storm
        ? dayjs(storm.date_of_storm).tz('America/New_York') // Convert to New York timezone
        : null,
      date_of_storm_end: storm.date_of_storm_end
        ? dayjs(storm.date_of_storm_end).tz('America/New_York') // Convert to New York timezone
        : null,
    });
    
    // Get the current threats associated with the storm
    if (storm.threats && Array.isArray(storm.threats)) {
      setStormThreats(storm.threats.map(threat => threat.threat_id));
    } else {
      setStormThreats([]);
    }
    
    // Fetch threats relevant to this storm's timeframe
    if (storm.date_of_storm && storm.date_of_storm_end) {
      fetchRelevantThreats(storm.date_of_storm, storm.date_of_storm_end);
    }
  
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingStorm(null);
    editForm.resetFields();
  };

  const handleEditSubmit = async (values) => {
    // Validation for start and end dates
    const startDateTime = values.date_of_storm;
    const endDateTime = values.date_of_storm_end;
  
    if (!startDateTime || !endDateTime) {
      message.error("Please provide both start and end date/time.");
      return;
    }
  
    if (dayjs(endDateTime).isBefore(dayjs(startDateTime))) {
      message.error("The end date/time cannot be before the start date/time.");
      return;
    }
  
    try {
      const updatedStorm = {
        ...editingStorm,
        ...values,
        region: values.region,
        storm_strat_sent: values.storm_strat_sent || false,
        date_of_storm: startDateTime
          ? startDateTime.tz('UTC').format('YYYY-MM-DDTHH:mm:ss[Z]')
          : null,
        date_of_storm_end: endDateTime
          ? endDateTime.tz('UTC').format('YYYY-MM-DDTHH:mm:ss[Z]')
          : null,
      };
  
      // Submit updated storm to the API
      await api.patch(`/dispatch/storm/${editingStorm.storm_id}/`, updatedStorm);
      message.success("Storm updated successfully");
      
      // Update storm threats if they have changed
      await updateStormThreats(editingStorm.storm_id, stormThreats);
      
      setIsEditModalVisible(false);
      fetchStorms(); // Refresh the storm list
    } catch (error) {
      console.error("Failed to update storm:", error);
      message.error("Failed to update storm");
    }
  };

  // Fetch sites from the WeatherWorks API
  const fetchThreats = async () => {
    setLoadingThreats(true);
    try {
      const response = await api.get('/weatherworks/fetch-threats/');
      setThreats(response.data.threat_id_map || []);
      message.success('Threats fetched successfully!');
    } catch (error) {
      console.error('Error fetching threats:', error);
      message.error('Failed to fetch threats.');
    } finally {
      setLoadingThreats(false);
    }
  };

  const saveThreatsToDatabase = async () => {
    setSavingThreats(true);
    try {
      const response = await api.post('/weatherworks/save-threats/', {
        threats: selectedThreats,
      });
      message.success('Threats saved to database successfully!');
      setSelectedThreats([]);
    } catch (error) {
      console.error('Error saving threats:', error);
      message.error('Failed to save threats.');
    } finally {
      setSavingThreats(false);
    }
  };

  const importThreats = async () => {
    setImportingThreats(true);
    try {
      const response = await api.post('/weatherworks/import-threats-to-storms/', {
        threats: selectedThreats,
      });
      message.success(response.data.message);
      message.warning({
        content: "Please make sure to edit imported storms' severities.",
        duration: 7,
      });
      setSelectedThreats([]);
      fetchStorms();
    } catch (error) {
      console.error('Error importing threats:', error);
      message.error('Failed to import threats.');
    } finally {
      setImportingThreats(false);
    }
  };

  const handleThreatSelection = (threatId) => {
    setSelectedThreats((prevSelected) =>
      prevSelected.includes(threatId)
        ? prevSelected.filter((id) => id !== threatId)
        : [...prevSelected, threatId]
    );
  };

  const showViewDetailsModal = (storm) => {
    setSelectedStorm(storm);
    setIsViewDetailsModalVisible(true);
  };

  const handleViewDetailsCancel = () => {
    setIsViewDetailsModalVisible(false);
    setSelectedStorm(null);
    setSelectedStorms([]);
  };

  const fetchWeatherWorksStorms = async () => {
    if (!year) {
      message.error("Please select a year.");
      return;
    }

    setLoadingStorms(true);
    try {
      const response = await api.post("/weatherworks/fetch-storms-for-year/", { year });
      setFetchedStorms(response.data.storms);
      message.success("Storms fetched successfully!");
    } catch (error) {
      console.error("Error fetching storms:", error);
      message.error("Failed to fetch storms.");
    } finally {
      setLoadingStorms(false);
    }
  };

  const renderStorms = () => {
    if (!fetchedStorms) return null;
  
    const stormItems = [];
  
    Object.entries(fetchedStorms).forEach(([locationId, data]) => {
      const { location, storms } = data;
      storms.forEach((storm) => {
        const stormKey = storm.storm_location_id;
        stormItems.push({
          key: stormKey,
          city: location.city,
          start: dayjs(storm.start).tz("America/New_York").format("YYYY-MM-DD hh:mm A z"),
          end: dayjs(storm.end).tz("America/New_York").format("YYYY-MM-DD hh:mm A z"),
        });
      });
    });
  
    const filteredStormItems = stormItems.filter((item) =>
      item.city.toLowerCase().includes(searchCity.toLowerCase())
    );
  
    return (
      <List
        bordered
        dataSource={filteredStormItems}
        renderItem={(item) => (
          <List.Item>
            <Checkbox
              checked={selectedStorms.includes(item.key)}
              onChange={() => handleStormSelection(item.key)}
              style={{ width: "100%" }}
            >
              <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.5" }}>
                <span>
                  City: {item.city}
                </span>
                <span>
                  Start: {item.start}
                </span>
                <span>
                  End: {item.end}
                </span>
              </div>
            </Checkbox>
          </List.Item>
        )}
        style={{ maxHeight: 400, overflowY: "auto" }}
      />
    );
  };

  const handleStormSelection = (stormId) => {
    setSelectedStorms((prevSelected) =>
      prevSelected.includes(stormId)
        ? prevSelected.filter((id) => id !== stormId)
        : [...prevSelected, stormId]
    );
  };

  const handleConfirmSelection = async () => {
    if (selectedStorms.length !== 1) {
      message.error("Please select exactly one storm.");
      return;
    }
  
    const weatherworksId = selectedStorms[0]; // ID of the fetched WeatherWorks storm
    const stormId = selectedStorm?.storm_id; // ID of the currently selected storm in the modal
  
    if (!stormId) {
      message.error("No storm selected.");
      return;
    }
  
    if (!year) {
      message.error("Please select a year.");
      return;
    }
  
    // Locate the selected storm details
    const selectedStormDetails = Object.entries(fetchedStorms).find(([locationId, data]) =>
      data.storms.some(storm => storm.storm_location_id === weatherworksId)
    );
  
    if (!selectedStormDetails) {
      message.error("Storm details could not be found.");
      return;
    }
  
    const { location, storms } = selectedStormDetails[1];
  
    // Special handling for city and year
    const stormCity = location.city; // Extract city from location
    const stormYear = year; // Use the year selected by the user in the UI
  
    // Locate the specific storm details
    const stormDetails = storms.find(storm => storm.storm_location_id === weatherworksId);
  
    try {
      const response = await api.post(`/weatherworks/storm/${stormId}/update-storm-weatherworks-id/`, {
        weatherworks_id: weatherworksId,
        storm_year: stormYear,
        storm_city: stormCity,
        storm_start_weatherworks: stormDetails.start,
        storm_end_weatherworks: stormDetails.end,
        storm_precip_type_weatherworks: stormDetails.precip_type,
        storm_snow_amount_weatherworks: stormDetails.snow_amount,
        storm_snow_trace_weatherworks: stormDetails.snow_trace,
        storm_freezing_rain_amount_weatherworks: stormDetails.freezing_rain_amount,
        storm_freezing_rain_trace_weatherworks: stormDetails.freezing_rain_trace,
      });
  
      if (response.data.success) {
        message.success("Storm updated successfully.");
        setIsViewDetailsModalVisible(false);
        fetchStorms();
        setSelectedStorms([]);
      } else {
        message.error(response.data.message || "Failed to update storm.");
      }
    } catch (error) {
      console.error("Error updating storm:", error);
      message.error("Failed to update storm.");
    }
  };

  const handleUnmatchStorm = async () => {
    try {
      const response = await api.post(`/weatherworks/storm/${selectedStorm.storm_id}/unmatch/`);
      if (response.data.success) {
        message.success("Storm unmatched successfully.");
        setIsViewDetailsModalVisible(false);
        fetchStorms(); // Refresh the storm list
      } else {
        message.error(response.data.message || "Failed to unmatch storm.");
      }
    } catch (error) {
      console.error("Error unmatching storm:", error);
      message.error("Failed to unmatch storm.");
    }
  };

  const handleRefresh = () => {
    fetchStorms();
    fetchRegions();
  };

  // Get severity class for storm styling
  const getSeverityClass = (severity) => {
    switch(severity) {
      case 'light': return styles.lightStorm;
      case 'medium': return styles.mediumStorm;
      case 'heavy': return styles.heavyStorm;
      default: return '';
    }
  };

  const columns = [
    {
      title: 'Storm Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => capitalize(text),
      width: 120,
    },
    {
      title: 'Storm Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (text) => {
        if (text === "N/A") {
          return (
            <Tag color="red">N/A*</Tag>
          );
        }
        return (
          <Tag 
            className={`${styles.severityTag} ${getSeverityClass(text)}`}>
            {capitalize(text)}
          </Tag>
        );
      },
      sorter: (a, b, sortOrder) => {
        const ascendingOrder = ["N/A", "light", "medium", "heavy"];
        const descendingOrder = ["N/A", "heavy", "medium", "light"];
    
        const sortIndexA = (sortOrder === "ascend" ? ascendingOrder : descendingOrder).indexOf(a.severity);
        const sortIndexB = (sortOrder === "ascend" ? ascendingOrder : descendingOrder).indexOf(b.severity);
    
        return sortIndexA - sortIndexB;
      },
      width: 140,
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      render: (text) => (
        <Tag color="blue">{capitalize(text || "N/A")}</Tag>
      ),
      width: 120,
    },
    {
      title: (
        <span>
          Strategy 
          <Tooltip title="Whether storm strategy has been sent for this storm">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'storm_strat_sent',
      key: 'storm_strat_sent',
      render: (value) => (
        <Tag color={value ? "green" : "orange"}>
          {value ? "Sent" : "Not Sent"}
        </Tag>
      ),
      width: 120,
    },
    {
      title: (
        <span>
          Threats
          <Tooltip title="WeatherWorks threats associated with this storm">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'threats',
      key: 'threats',
      render: (threats) => {
        if (!threats || threats.length === 0) {
          return <Tag color="default">None</Tag>;
        }
        
        return (
          <div>
            {threats.slice(0, 2).map(threat => (
              <Tag 
                color="purple" 
                key={threat.threat_id}
                style={{ marginBottom: '5px' }}
              >
                {threat.weather_primary || 'Unknown'} - {dayjs(threat.start_date).format('MM/DD')}
              </Tag>
            ))}
            {threats.length > 2 && (
              <Tooltip 
                title={
                  <div>
                    {threats.map(threat => (
                      <div key={threat.threat_id} style={{ marginBottom: '5px' }}>
                        <strong>{threat.weather_primary || 'Unknown'}</strong> 
                        <div>Location: {threat.site_address || 'Unknown'}</div>
                        <div>Start: {dayjs(threat.start_date).format('MM/DD/YYYY hh:mm A')}</div>
                      </div>
                    ))}
                  </div>
                }
              >
                <Tag color="purple">+{threats.length - 2} more</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
      width: 150,
    },
    {
      title: 'Date of Storm',
      dataIndex: 'date_of_storm',
      key: 'date_of_storm',
      render: (utcDate) => {
        return utcDate
          ? dayjs(utcDate).tz('America/New_York').format('MM/DD/YY HH:mm')
          : 'N/A';
      },
      width: 140,
    },
    {
      title: 'Date of Storm End',
      dataIndex: 'date_of_storm_end',
      key: 'date_of_storm_end',
      render: (utcDate) => {
        return utcDate
          ? dayjs(utcDate).tz('America/New_York').format('MM/DD/YY HH:mm')
          : 'N/A';
      },
      width: 140,
    },
    {
      title: 'Date Created',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (utcDate) => {
        return utcDate
          ? dayjs(utcDate).tz('America/New_York').format('MM/DD/YY HH:mm')
          : 'N/A';
      },
      width: 140,
    },
    {
      title: 'Matched',
      key: 'matched',
      render: (storm) => (
        <Checkbox checked={storm.storm_location_id_weatherworks !== null} disabled />
      ),
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (storm) => (
        <Space>
          <Button
            type="primary"
            onClick={() => showEditModal(storm)}
            icon={<EditOutlined />}
            className={styles.actionButton}
            size="small"
          />
          <Button
            type="primary"
            onClick={() => showViewDetailsModal(storm)}
            icon={<InfoCircleOutlined />}
            className={styles.actionButton}
            size="small"
          />
        </Space>
      ),
      width: 100,
    },
  ];


  const applyFilters = () => {
    const filtered = storms.filter((storm) => {
      const filterStormType = searchStormType ? storm.type === searchStormType : true;
      const filterStormSeverity = searchStormSeverity ? storm.severity === searchStormSeverity : true;
      const filterRegion = searchRegion ? storm.region === searchRegion : true;
      return filterStormType && filterStormSeverity && filterRegion;
    });
    setFilteredStorms(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [searchStormType, searchStormSeverity, searchRegion]);

  const verifyThreat = async (threatId, verified) => {
    setVerifyingThreat(true);
    try {
      const response = await api.post(`/weatherworks/verify-threat/${threatId}/`, {
        verified: verified
      });
      
      if (response.data.success) {
        // Update the local state with the updated threat
        const updatedThreats = savedThreats.map(threat => {
          if (threat.threat_id === threatId) {
            return { ...threat, verified };
          }
          return threat;
        });
        setSavedThreats(updatedThreats);
        
        message.success(response.data.message);
      } else {
        message.error(response.data.message || 'Failed to verify threat');
      }
    } catch (error) {
      console.error('Error verifying threat:', error);
      message.error('Failed to verify threat');
    } finally {
      setVerifyingThreat(false);
    }
  };

  const updateStormThreats = async (stormId, threatIds) => {
    try {
      const response = await api.put(`/dispatch/storm/${stormId}/threats/`, {
        threat_ids: threatIds
      });
      
      if (response.data.success) {
        message.success('Storm threats updated successfully');
        fetchStorms(); // Refresh the storm list to show updated threats
      } else {
        message.error(response.data.message || 'Failed to update storm threats');
      }
    } catch (error) {
      console.error('Error updating storm threats:', error);
      message.error('Failed to update storm threats');
    }
  };

  const fetchRelevantThreats = async (startDate, endDate) => {
    setLoadingRelevantThreats(true);
    try {
      const response = await api.post('/weatherworks/threats-by-timeframe/', {
        start_date: startDate,
        end_date: endDate
      });
      
      if (response.data.success) {
        setRelevantThreats(response.data.threats);
      }
    } catch (error) {
      console.error('Error fetching relevant threats:', error);
      message.error('Failed to fetch relevant threats');
    } finally {
      setLoadingRelevantThreats(false);
    }
  };

  const handleStormDateChange = () => {
    const startDateTime = editForm.getFieldValue('date_of_storm');
    const endDateTime = editForm.getFieldValue('date_of_storm_end');
    
    if (startDateTime && endDateTime) {
      // Format dates for API
      const formattedStartDate = startDateTime.tz('UTC').format('YYYY-MM-DDTHH:mm:ss[Z]');
      const formattedEndDate = endDateTime.tz('UTC').format('YYYY-MM-DDTHH:mm:ss[Z]');
      
      // Fetch threats relevant to this timeframe
      fetchRelevantThreats(formattedStartDate, formattedEndDate);
    }
  };

  const viewSavedThreatDetails = (threat) => {
    // Format dates for better display
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(date);
    };
    
    // Determine tag color based on weather type
    const getTagColor = (type) => {
      const typeColors = {
        WIND: '#f50',
        HURRICANE: '#f50',
        RAIN: '#108ee9',
        SURGE: '#87d068',
        TORNADO: '#722ed1',
        FLOOD: '#108ee9',
        SNOW: '#2db7f5',
        ICE: '#2db7f5',
        DEFAULT: '#8c8c8c'
      };
      return typeColors[type] || typeColors.DEFAULT;
    };
    
    const weatherType = threat.weather_primary || 'UNKNOWN';
    
    const weatherTypeTag = (
      <Tag 
        color={getTagColor(weatherType)} 
        className={styles.detailsModalTag}
      >
        {weatherType}
      </Tag>
    );

    // Create a nicely formatted title with tag
    const title = (
      <div className={styles.detailsModalTitle}>
        {weatherTypeTag}
        <span>Threat Details</span>
      </div>
    );
    
    // Format the verification status badge
    const verificationStatus = threat.verified ? (
      <Tag icon={<SafetyCertificateOutlined />} color="success">Verified</Tag>
    ) : (
      <Tag icon={<ExclamationCircleOutlined />} color="warning">Unverified</Tag>
    );
    
    // Create the content for the modal
    const content = (
      <div className={styles.threatDetailModal}>
        <div className={styles.threatDetailHeader}>
          <div className={styles.threatDetailStatus}>
            {verificationStatus}
            <span className={styles.threatDetailId}>ID: {threat.threat_id || 'Unknown'}</span>
          </div>
          
          <div className={styles.threatDetailLocation}>
            <EnvironmentOutlined className={styles.locationIcon} />
            <div>
              {threat.site_address || 'Unknown location'}
              {threat.latitude && threat.longitude && (
                <div>{`${threat.latitude.toFixed(4)}, ${threat.longitude.toFixed(4)}`}</div>
              )}
            </div>
          </div>
        </div>
        
        <div className={styles.threatDetailTimeline}>
          <div className={styles.threatDetailTimepoint}>
            <span className={styles.timeLabel}>Start Time</span>
            <span className={styles.timeValue}>
              {threat.start_date ? dayjs(threat.start_date).format('MMM DD, YYYY') : 'N/A'}
            </span>
            <span className={styles.timeDetail}>
              <ClockCircleOutlined /> {threat.start_date ? dayjs(threat.start_date).format('HH:mm A') : 'Unknown'}
            </span>
          </div>
          
          <div className={styles.timelineDivider}></div>
          
          <div className={styles.threatDetailTimepoint}>
            <span className={styles.timeLabel}>End Time</span>
            <span className={styles.timeValue}>
              {threat.end_date ? dayjs(threat.end_date).format('MMM DD, YYYY') : 'N/A'}
            </span>
            <span className={styles.timeDetail}>
              <ClockCircleOutlined /> {threat.end_date ? dayjs(threat.end_date).format('HH:mm A') : 'Unknown'}
            </span>
          </div>
        </div>
        
        <div className={styles.threatDetailData}>
          <div className={styles.threatDataSection}>
            <div className={styles.sectionTitle}>
              <RadarChartOutlined /> Weather Details
            </div>
            <div className={styles.dataGrid}>
              <div className={styles.dataItem}>
                <span className={styles.dataLabel}>Weather Type</span>
                <span className={styles.dataValue}>{threat.weather_primary || 'Unknown'}</span>
              </div>
              <div className={styles.dataItem}>
                <span className={styles.dataLabel}>Total Snow</span>
                <span className={styles.dataValue}>{threat.total_snow ? `${threat.total_snow} in` : 'N/A'}</span>
              </div>
              <div className={styles.dataItem}>
                <span className={styles.dataLabel}>Total Ice</span>
                <span className={styles.dataValue}>{threat.total_ice ? `${threat.total_ice} in` : 'N/A'}</span>
              </div>
              <div className={styles.dataItem}>
                <span className={styles.dataLabel}>Duration (Hours)</span>
                <span className={styles.dataValue}>{threat.hours || 'N/A'}</span>
              </div>
            </div>
          </div>
          
          <div className={styles.threatDataSection}>
            <div className={styles.sectionTitle}>
              <InfoCircleFilled /> Additional Information
            </div>
            <div className={styles.dataGrid}>
              <div className={styles.dataItem}>
                <span className={styles.dataLabel}>External ID</span>
                <span className={styles.dataValue}>{threat.external_id || 'N/A'}</span>
              </div>
              <div className={styles.dataItem}>
                <span className={styles.dataLabel}>Created At</span>
                <span className={styles.dataValue}>
                  {threat.created_at ? dayjs(threat.created_at).format('YYYY-MM-DD HH:mm') : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    Modal.info({
      icon: null,
      title,
      content,
      width: 650,
      className: styles.detailsInfoModal,
      okText: 'Close',
    });
  };

  return (
    <div className={styles.container}>
      {/* View Details Modal */}
      <Modal
        title="View Storm Details"
        visible={isViewDetailsModalVisible}
        onCancel={handleViewDetailsCancel}
        footer={null}
        className={styles.modalStyle}
      >
        {selectedStorm?.storm_location_id_weatherworks ? (
          <div className={styles.modalContent}>
            <h3 className={styles.modalSectionTitle}>Storm details from matched WeatherWorks storm</h3>
            <div className={styles.detailGrid}>
              <p><strong>Location ID:</strong> {selectedStorm.storm_location_id_weatherworks || "N/A"}</p>
              <p><strong>Season:</strong> {selectedStorm.storm_year_weatherworks || "N/A"}</p>
              <p><strong>City:</strong> {selectedStorm.storm_city_weatherworks || "N/A"}</p>
              <p><strong>Start Time:</strong> {selectedStorm.storm_start_weatherworks ? dayjs(selectedStorm.storm_start_weatherworks).tz("America/New_York").format("YYYY-MM-DD hh:mm A z") : "N/A"}</p>
              <p><strong>End Time:</strong> {selectedStorm.storm_end_weatherworks ? dayjs(selectedStorm.storm_end_weatherworks).tz("America/New_York").format("YYYY-MM-DD hh:mm A z") : "N/A"}</p>
              <p><strong>Precipitation Type:</strong> {selectedStorm.storm_precip_type_weatherworks || "N/A"}</p>
              <p><strong>Snow Amount:</strong> {selectedStorm.storm_snow_amount_weatherworks !== null && selectedStorm.storm_snow_amount_weatherworks !== undefined ? selectedStorm.storm_snow_amount_weatherworks : "N/A"}</p>
              <p><strong>Snow Trace:</strong> {selectedStorm.storm_snow_trace_weatherworks || "N/A"}</p>
              <p><strong>Freezing Rain Amount:</strong> {selectedStorm.storm_freezing_rain_amount_weatherworks !== null && selectedStorm.storm_freezing_rain_amount_weatherworks !== undefined ? selectedStorm.storm_freezing_rain_amount_weatherworks : "N/A"}</p>
              <p><strong>Freezing Rain Trace:</strong> {selectedStorm.storm_freezing_rain_trace_weatherworks || "N/A"}</p>
            </div>
            
            {/* Associated Threats Section */}
            {selectedStorm.threats && selectedStorm.threats.length > 0 && (
              <>
                <h3 className={styles.modalSectionTitle}>Associated Threats</h3>
                <div className={styles.threatsList}>
                  {selectedStorm.threats.map(threat => (
                    <div key={threat.threat_id} className={styles.threatItem}>
                      <div className={styles.threatHeader}>
                        <Tag 
                          color="purple" 
                          className={styles.threatTag}
                        >
                          {threat.weather_primary || 'Unknown'}
                        </Tag>
                        <div className={styles.verificationStatus}>
                          <Checkbox 
                            checked={threat.verified} 
                            onChange={(e) => verifyThreat(threat.threat_id, e.target.checked)}
                          />
                          <span>Verified</span>
                        </div>
                      </div>
                      <div className={styles.threatDetails}>
                        <p><strong>Site:</strong> {threat.site_address || "N/A"}</p>
                        <p>
                          <strong>Duration:</strong> {dayjs(threat.start_date).format('MMM DD, YYYY HH:mm')} - {dayjs(threat.end_date).format('MMM DD, YYYY HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            <Popconfirm
              title="Are you sure you want to unmatch this storm?"
              onConfirm={handleUnmatchStorm}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                className={styles.unmatchButton}
              >
                Unmatch Storm
              </Button>
            </Popconfirm>
          </div>
          
        ) : (
          <>
            <div className={styles.modalInfoText}>
              Please choose the season you wish to begin the range from. All storms will be from the selected year to the next.
            </div>

            {/* Year selection dropdown */}
            <Select
              placeholder="Select Year"
              onChange={(value) => setYear(value)}
              style={{ width: "100%", marginBottom: 20 }}
              className={styles.selectInput}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <Select.Option key={y} value={y}>
                  {y}
                </Select.Option>
              ))}
            </Select>

            {/* Fetch Storms Button */}
            <Button
              type="primary"
              onClick={fetchWeatherWorksStorms}
              loading={loadingStorms}
              className={styles.primaryButton}
            >
              Fetch Storms - WeatherWorks
            </Button>

            {/* Conditionally render City Search Filter */}
            {fetchedStorms && (
              <Input
                placeholder="Search by city"
                allowClear
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                style={{ marginBottom: 20 }}
                className={styles.searchInput}
              />
            )}

            {/* Storms List */}
            {loadingStorms ? '' : renderStorms()}

            {/* Confirm Selection Button */}
            <Button
              type="primary"
              disabled={selectedStorms.length !== 1}
              className={styles.primaryButton}
              onClick={handleConfirmSelection}
            >
              Confirm Selection
            </Button>
          </>
        )}
      </Modal>

      <div className={styles.header}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={2} className={styles.pageTitle}>
              <ThunderboltOutlined /> Storms Management
            </Title>
            <Text className={styles.subheading}>
              Track and manage storm events and their strategies
            </Text>
          </Col>
          <Col>
            <Tooltip title="Refresh storm data">
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                className={styles.refreshButton}
              >
                Refresh
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </div>

      <Card className={styles.filterCard}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8} lg={8}>
            <Text strong className={styles.filterLabel}>Storm Type:</Text>
            <Select
              placeholder="Search storm type"
              value={searchStormType}
              onChange={(value) => setSearchStormType(value)}
              style={{ width: '100%' }}
              allowClear
              className={styles.selectFilter}
            >
              {Array.from(new Set(storms.map((storm) => storm.type)))
                .map((type) => (
                <Option key={type} value={type}>
                  {capitalize(type)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8} lg={8}>
            <Text strong className={styles.filterLabel}>Storm Severity:</Text>
            <Select
              placeholder="Search storm severity"
              value={searchStormSeverity}
              onChange={(value) => setSearchStormSeverity(value)}
              style={{ width: '100%' }}
              allowClear
              className={styles.selectFilter}
            >
              {Array.from(new Set(storms.map((storm) => storm.severity)))
                .map((severity) => (
                <Option key={severity} value={severity}>
                  {capitalize(severity)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8} lg={8}>
            <Text strong className={styles.filterLabel}>Region:</Text>
            <Select
              placeholder="Search region"
              value={searchRegion}
              onChange={(value) => setSearchRegion(value)}
              style={{ width: '100%' }}
              allowClear
              className={styles.selectFilter}
            >
              {Array.from(new Set(storms.map((storm) => storm.region)))
                .filter(region => region) // Filter out null/undefined values
                .map((region) => (
                <Option key={region} value={region}>
                  {capitalize(region)}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        
        <div className={styles.warningText}>
          <InfoCircleOutlined style={{ color: 'red', marginRight: 5 }} />
          <Text>Please edit the severity of any N/A severity storms.</Text>
        </div>
      </Card>

      <Card className={styles.tableCard}>
        <div className={styles.tableContainer}>
          <Table 
            dataSource={filteredStorms} 
            columns={columns} 
            rowKey="storm_id" 
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
            }} 
            scroll={{ x: 1100 }}
            className={styles.dataTable}
            rowClassName={(record) => {
              return record.storm_strat_sent ? styles.stratSentRow : '';
            }}
            bordered={true}
            size="middle"
          />
        </div>
      </Card>

      {/* Edit Storm Modal */}
      <Modal
        title={
          <div className={styles.modalTitle}>
            <EditOutlined /> Edit Storm
          </div>
        }
        visible={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        className={styles.modalStyle}
      >
        <Form form={editForm} onFinish={handleEditSubmit} layout="vertical" className={styles.editForm}>
          <Form.Item
            name="type"
            label="Storm Type"
            rules={[{ required: false, message: 'Please enter the storm type' }]}
          >
            <Input className={styles.formInput} />
          </Form.Item>
          <Form.Item
            name="severity"
            label="Storm Severity"
            rules={[{ required: false, message: 'Please enter the storm severity' }]}
          >
            <Select placeholder="Select severity" className={styles.formSelect}>
              <Option value="light">Light</Option>
              <Option value="medium">Medium</Option>
              <Option value="heavy">Heavy</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="region"
            label="Region"
            rules={[{ required: false, message: 'Please select the region' }]}
          >
            <Select placeholder="Select region" className={styles.formSelect}>
              {regions.map(region => (
                <Option key={region} value={region}>
                  {capitalize(region)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="storm_strat_sent"
            label="Storm Strategy Sent"
            valuePropName="checked"
          >
            <Checkbox className={styles.formCheckbox}>Strategy has been sent</Checkbox>
          </Form.Item>
          <Form.Item
            name="date_of_storm"
            label="Date and Time of Storm"
            rules={[{ required: true, message: 'Please enter the date and time of the storm' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              showTime={{
                format: "HH:mm",
                defaultValue: dayjs("00:00", "HH:mm"), // Default time is 00:00
              }}
              format="YYYY-MM-DD HH:mm" // Display format for both date and time
              allowClear={false} // Disable clearing to ensure a value is always present
              className={styles.formDatePicker}
              onChange={handleStormDateChange}
            />
          </Form.Item>
          <Form.Item
            name="date_of_storm_end"
            label="Date and Time of Storm End"
            rules={[{ required: true, message: 'Please enter the end date and time of the storm' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              showTime={{
                format: "HH:mm",
                defaultValue: dayjs("00:00", "HH:mm"), // Default time is 00:00
              }}
              format="YYYY-MM-DD HH:mm" // Display format for both date and time
              allowClear={false} // Disable clearing to ensure a value is always present
              className={styles.formDatePicker}
              onChange={handleStormDateChange}
            />
          </Form.Item>
          
          <Form.Item
            label="Associated Threats"
            help="Select threats that occurred during this storm's timeframe"
          >
            <div className={styles.threatSelectionContainer}>
              {loadingRelevantThreats ? (
                <div className={styles.loadingContainer}>
                  <Spin /> <span>Loading relevant threats...</span>
                </div>
              ) : relevantThreats.length > 0 ? (
                <div className={styles.threatCheckboxGroup}>
                  {relevantThreats.map(threat => (
                    <Checkbox
                      key={threat.threat_id}
                      checked={stormThreats.includes(threat.threat_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setStormThreats([...stormThreats, threat.threat_id]);
                        } else {
                          setStormThreats(stormThreats.filter(id => id !== threat.threat_id));
                        }
                      }}
                      className={styles.threatCheckboxItem}
                    >
                      <div className={styles.threatCheckboxContent}>
                        <div className={styles.threatCheckboxTitle}>
                          <Tag color="purple">{threat.weather_primary || 'Unknown'}</Tag>
                          <span className={styles.threatLocation}>{threat.site_address}</span>
                        </div>
                        <div className={styles.threatCheckboxDetails}>
                          {dayjs(threat.start_date).format('MMM DD, YYYY HH:mm')} - {dayjs(threat.end_date).format('MMM DD, YYYY HH:mm')}
                          {threat.verified && <Tag color="green" style={{ marginLeft: 8 }}>Verified</Tag>}
                        </div>
                      </div>
                    </Checkbox>
                  ))}
                </div>
              ) : (
                <div className={styles.noThreatsMessage}>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  No saved threats found for this timeframe. You can save threats from the threat section below.
                </div>
              )}
            </div>
          </Form.Item>
          <Form.Item className={styles.formActions}>
            <Button 
              type="primary" 
              htmlType="submit"
              className={styles.submitButton}
            >
              Update Storm
            </Button>
            <Button onClick={handleEditCancel} className={styles.cancelButton}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Threats Section */}
      <Card className={styles.threatsCard} title={
        <div className={styles.cardTitle}>
          <ThunderboltOutlined /> Threats to Storms
        </div>
      }>
        <Tabs defaultActiveKey="1" className={styles.threatTabs}>
          <Tabs.TabPane 
            tab={
              <span className={styles.tabHeader}>
                <PlusOutlined /> Select Threats to Import as Storms
              </span>
            } 
            key="1"
          >
            <div className={styles.tabContent}>
              <div className={styles.actionHeader}>
                <Button
                  onClick={fetchThreats}
                  type="primary"
                  className={styles.fetchButton}
                  loading={loadingThreats}
                  icon={<CloudOutlined />}
                >
                  {loadingThreats ? 'Fetching...' : 'Fetch Threats'}
                </Button>
                <Text type="secondary" className={styles.actionHelp}>
                  <InfoCircleOutlined /> Fetch the latest threats from WeatherWorks
                </Text>
              </div>
              <List
                bordered
                dataSource={Object.entries(threats)}
                renderItem={([threatId, threatData]) => (
                  <List.Item className={styles.threatItem}>
                    <Checkbox
                      checked={selectedThreats.includes(threatId)}
                      onChange={() => handleThreatSelection(threatId)}
                      className={styles.threatCheckbox}
                    >
                      <div className={styles.checkboxContent}>
                        <div className={styles.threatPrimary}>
                          <Tag color="purple">{threatData.weatherPrimary || 'Unknown'}</Tag>
                          <span className={styles.threatLocation}>{threatData.siteAddress}</span>
                        </div>
                        <div className={styles.threatTiming}>
                          <span><b>Start:</b> {dayjs(threatData.startDate).tz('America/New_York').format('MM/DD/YY HH:mm')}</span>
                          <span className={styles.timingSeparator}>•</span>
                          <span><b>End:</b> {dayjs(threatData.endDate).tz('America/New_York').format('MM/DD/YY HH:mm')}</span>
                        </div>
                      </div>
                    </Checkbox>
                  </List.Item>
                )}
                className={styles.threatList}
                locale={{ emptyText: 'No threats available. Click "Fetch Threats" to load data.' }}
              />
              <div className={styles.threatButtonContainer}>
                <Button
                  type="primary"
                  onClick={saveThreatsToDatabase}
                  disabled={selectedThreats.length === 0}
                  className={styles.saveButton}
                  loading={savingThreats}
                  icon={<SaveOutlined />}
                >
                  {savingThreats ? 'Saving...' : `Save ${selectedThreats.length} Threat${selectedThreats.length !== 1 ? 's' : ''}`}
                </Button>
                <Button
                  type="primary"
                  onClick={importThreats}
                  disabled={selectedThreats.length === 0}
                  className={styles.importButton}
                  loading={importingThreats}
                  icon={<ThunderboltOutlined />}
                >
                  {importingThreats ? 'Importing...' : `Import as Storm${selectedThreats.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          </Tabs.TabPane>
          
          <Tabs.TabPane 
            tab={
              <span className={styles.tabHeader}>
                <CloudOutlined /> Saved Threats Database
              </span>
            } 
            key="2"
          >
            <div className={styles.tabContent}>
              <div className={styles.actionHeader}>
                <Button
                  onClick={fetchSavedThreats}
                  type="primary"
                  className={styles.fetchButton}
                  loading={loadingSavedThreats}
                  icon={<ReloadOutlined />}
                >
                  {loadingSavedThreats ? 'Loading...' : 'Refresh Saved Threats'}
                </Button>
                <Text type="secondary" className={styles.actionHelp}>
                  <InfoCircleOutlined /> View and manage threats saved in the database
                </Text>
              </div>
              <Table
                loading={loadingSavedThreats}
                dataSource={savedThreats}
                rowKey="threat_id"
                className={styles.savedThreatsTable}
                pagination={{
                  pageSize: 5,
                  showSizeChanger: true,
                  pageSizeOptions: ['5', '10', '20'],
                }}
                columns={[
                  {
                    title: 'Type',
                    dataIndex: 'weather_primary',
                    key: 'weather_primary',
                    render: text => <Tag color="purple">{text || 'Unknown'}</Tag>,
                  },
                  {
                    title: 'Location',
                    dataIndex: 'site_address',
                    key: 'site_address',
                    ellipsis: true,
                  },
                  {
                    title: 'Start Date',
                    dataIndex: 'start_date',
                    key: 'start_date',
                    render: date => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A',
                    sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
                  },
                  {
                    title: 'End Date',
                    dataIndex: 'end_date',
                    key: 'end_date',
                    render: date => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A',
                    sorter: (a, b) => new Date(a.end_date) - new Date(b.end_date),
                  },
                  {
                    title: 'Verified',
                    dataIndex: 'verified',
                    key: 'verified',
                    render: (verified, record) => (
                      <Checkbox 
                        checked={verified} 
                        onChange={(e) => verifyThreat(record.threat_id, e.target.checked)}
                        disabled={verifyingThreat}
                      />
                    ),
                    filters: [
                      { text: 'Verified', value: true },
                      { text: 'Not Verified', value: false },
                    ],
                    onFilter: (value, record) => record.verified === value,
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_, record) => (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => viewSavedThreatDetails(record)}
                      >
                        View Details
                      </Button>
                    ),
                  },
                ]}
                bordered
                size="middle"
              />
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default StormsListView;